import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  User,
  Mail,
  Building2,
  Save,
  Loader2,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SettingsPage() {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [concession, setConcession] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || ""
      }));
      
      if (user.concession_id) {
        fetchConcession();
      }
    }
  }, [user]);

  const fetchConcession = async () => {
    try {
      const res = await api.get(`/concessions/${user.concession_id}`);
      setConcession(res.data);
    } catch (error) {
      console.error("Failed to fetch concession");
    }
  };

  const handleSaveProfile = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Le nom et l'email sont obligatoires");
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        email: formData.email
      };
      
      await api.put(`/users/${user.id}`, updateData);
      
      // Update local storage
      const updatedUser = { ...user, name: formData.name, email: formData.email };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      toast.success("Profil mis à jour avec succès");
      
      // Reload to update context
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.newPassword) {
      toast.error("Veuillez saisir un nouveau mot de passe");
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (formData.newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, {
        password: formData.newPassword
      });
      
      toast.success("Mot de passe modifié avec succès");
      setFormData(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setSaving(false);
    }
  };

  const getRoleName = (role) => {
    const roles = {
      admin: "Administrateur",
      manager: "Responsable de concession",
      seller: "Vendeur",
      hr: "Ressources Humaines"
    };
    return roles[role] || role;
  };

  return (
    <Layout title="Paramètres du compte">
      <div className="max-w-2xl">
        {/* Profile Section */}
        <div className="form-section">
          <h3 className="form-section-title flex items-center gap-2">
            <User className="w-5 h-5" />
            Informations personnelles
          </h3>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Votre nom"
                data-testid="settings-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Adresse email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="votre@email.com"
                  className="pl-10"
                  data-testid="settings-email-input"
                />
              </div>
              <p className="text-sm text-gray-500">
                Cette adresse sera utilisée pour recevoir les notifications et documents signés.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Rôle</Label>
              <Input
                value={getRoleName(user?.role)}
                disabled
                className="bg-gray-50"
              />
            </div>

            {concession && (
              <div className="space-y-2">
                <Label>Concession</Label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium">{concession.name}</p>
                    <p className="text-sm text-gray-500">{concession.brand} - {concession.city}</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleSaveProfile}
              className="btn-primary"
              disabled={saving}
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Password Section */}
        <div className="form-section">
          <h3 className="form-section-title flex items-center gap-2">
            <Key className="w-5 h-5" />
            Changer le mot de passe
          </h3>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nouveau mot de passe</Label>
              <Input
                id="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              onClick={handleChangePassword}
              variant="outline"
              disabled={saving || !formData.newPassword}
              data-testid="change-password-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Modification...
                </>
              ) : (
                "Changer le mot de passe"
              )}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
